import { ExportOrganisationRequest_Filters } from "lib/generated/app-service-gql/graphql";
import { useExportOrganisations } from "lib/hooks/api/organisations/useExportOrganisations";
import {
  SearchOrganisationsParams,
  useOrganisations,
} from "lib/hooks/api/organisations/useOrganisations";
import { EventDataTypes } from "lib/tracking";
import {
  ExportOrgCountField,
  ExportOrgSortBy,
  ExportOrgSortOrder,
  OrgSortBy,
  OrgSortOrder,
  SearchOrgRole,
} from "lib/types/graphQLEnums";
import { convertSupplierSortToOrganisationSort } from "../../lib/utils/supplierUtils";
import { ExportButtonGraphql } from "../actions/ExportButtonGraphql";
import { toast } from "../ui/toast/Toast";

type Props = {
  filters: SearchOrganisationsParams;
  countFields?: ExportOrgCountField[];
  selectedOrganisationIds?: string[];
};

export function ExportOrganisationsButton({
  filters,
  countFields = [ExportOrgCountField.NoticeCount],
  selectedOrganisationIds,
}: Props) {
  const effectiveRole = filters.role;
  // If there are selectedIds - use these as the export count instead of the search
  const hasSelectedIds = selectedOrganisationIds && selectedOrganisationIds.length > 0;
  // initial params are for counting all total results in table when we don't use selected IDs
  const initialParams: SearchOrganisationsParams = {
    ...filters,
    limit: filters.limit ?? 10,
    page: filters.page ?? 1,
    role: effectiveRole,
    sortOrder: filters.sortOrder as OrgSortOrder,
    sortBy:
      effectiveRole === SearchOrgRole.Supplier && typeof filters.sortBy === "string" // this is to handle the supplier sort field using the legacy type
        ? convertSupplierSortToOrganisationSort(filters.sortBy)
        : filters.sortBy,
  };

  const { isLoading: isInitialLoading, data: initialData } = useOrganisations(initialParams, {
    enabled: !hasSelectedIds,
  });

  const orgExportMutation = useExportOrganisations({
    onError: () => toast.error("Failed to download file"),
  });

  const exportCount = hasSelectedIds
    ? selectedOrganisationIds.length
    : initialData?.searchOrganisations.total ?? 0;

  const exportFilters: ExportOrganisationRequest_Filters = {
    anchorOrganisation: initialParams.anchorOrganisation,
    limit: exportCount,
    page: 1,
    ids: hasSelectedIds ? selectedOrganisationIds : initialParams.ids,
    names: initialParams.names,
    isSme: initialParams.isSme,
    listIds: initialParams.listIds,
    role: effectiveRole,
    categoriesPublicSectorOrgType: filters.pubSecOrgTypes,
    regionIds: filters.regionIds,
    countryCodes: filters.countryCodes,
    sortOrder: mapSortOrder(initialParams.sortOrder),
    sortBy: mapSortBy(initialParams.sortBy),
  };

  const dataType =
    effectiveRole === SearchOrgRole.Supplier ? EventDataTypes.supplier : EventDataTypes.buyer;

  return (
    <ExportButtonGraphql
      exportDataType={dataType}
      isLoadingTotalResults={hasSelectedIds ? false : isInitialLoading}
      totalResults={exportCount || 0}
      exportMutation={orgExportMutation}
      exportVariables={{
        request: { filters: exportFilters, countFields },
      }}
    />
  );
}

function mapSortOrder(order?: OrgSortOrder): ExportOrgSortOrder {
  return order === OrgSortOrder.Asc ? ExportOrgSortOrder.Asc : ExportOrgSortOrder.Desc;
}

function mapSortBy(sortBy?: OrgSortBy): ExportOrgSortBy | undefined {
  switch (sortBy) {
    case OrgSortBy.AwardCount:
      return ExportOrgSortBy.AwardCount;
    case OrgSortBy.NoticeCount:
      return ExportOrgSortBy.NoticeCount;
    default: {
      console.warn(`Unknown sortBy value: ${sortBy}`);
      return undefined;
    }
  }
}
